import React, { useState, useRef, useEffect } from 'react';
import { useWallet } from './WalletContext';
import { web } from '@klever/sdk-web';
import {
  FaTimes,
  FaRocket,
  FaCoins,
  FaPlus,
  FaTrash,
  FaPlay,
  FaPause,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaInfoCircle,
  FaWallet,
  FaLayerGroup,
  FaClock,
  FaUserShield,
  FaUsers,
  FaCalendarAlt,
  FaToggleOn,
  FaToggleOff,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';
import './ConfigITOModal.css';

const ConfigITOModal = ({ collection, existingIto, onClose, onSuccess }) => {
  const { walletAddress } = useWallet();
  const modalRef = useRef(null);

  // Section collapse states
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showWhitelist, setShowWhitelist] = useState(false);

  // Form state - Basic
  // NOTE: On KleverChain, pack price is PER NFT, not total
  // So Pack with 2 NFTs at 750 KLV = 750 KLV per NFT = 1500 KLV total
  const [formData, setFormData] = useState({
    receiverAddress: existingIto?.receiverAddress || walletAddress || '',
    currencyId: existingIto?.packData?.[0]?.key || 'KLV',
    maxAmount: existingIto?.maxAmount || collection?.maxSupply || 10000,
    status: existingIto?.isActive !== undefined ? (existingIto.isActive ? 1 : 0) : 1,
    // Price stored here is PER NFT
    packs: existingIto?.packData?.[0]?.packs?.map(p => ({
      amount: p.amount,
      pricePerNft: p.price / Math.pow(10, 6) // Price per NFT from blockchain
    })) || [
      { amount: 1, pricePerNft: 100 },   // 1 NFT @ 100 KLV each = 100 KLV total
      { amount: 5, pricePerNft: 90 },    // 5 NFTs @ 90 KLV each = 450 KLV total
      { amount: 10, pricePerNft: 80 }    // 10 NFTs @ 80 KLV each = 800 KLV total
    ],
    // Advanced options
    startTime: existingIto?.startTime ? new Date(existingIto.startTime * 1000).toISOString().slice(0, 16) : '',
    endTime: existingIto?.endTime ? new Date(existingIto.endTime * 1000).toISOString().slice(0, 16) : '',
    // Whitelist
    whitelistStatus: existingIto?.whitelistStatus || 0, // 0 = inactive, 1 = active
    whitelistStartTime: existingIto?.whitelistStartTime ? new Date(existingIto.whitelistStartTime * 1000).toISOString().slice(0, 16) : '',
    whitelistEndTime: existingIto?.whitelistEndTime ? new Date(existingIto.whitelistEndTime * 1000).toISOString().slice(0, 16) : '',
    whitelistAddresses: existingIto?.whitelistInfo?.map(w => ({
      address: w.address,
      limit: w.limit || 0
    })) || [],
    // Default limits
    defaultLimitPerAddress: existingIto?.defaultLimitPerAddress || 0, // 0 = unlimited
  });

  // New whitelist entry
  const [newWhitelistAddress, setNewWhitelistAddress] = useState('');
  const [newWhitelistLimit, setNewWhitelistLimit] = useState(0);

  // Transaction state
  const [transactionState, setTransactionState] = useState('');
  const [transactionMessage, setTransactionMessage] = useState('');
  const [errors, setErrors] = useState({});

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        if (transactionState !== 'processing') {
          onClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.body.classList.add('modal-open');

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.classList.remove('modal-open');
    };
  }, [onClose, transactionState]);

  // Update form field
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Update pack
  const updatePack = (index, field, value) => {
    setFormData(prev => {
      const newPacks = [...prev.packs];
      newPacks[index] = { ...newPacks[index], [field]: value };
      return { ...prev, packs: newPacks };
    });
  };

  // Add pack - with discount on price per NFT
  const addPack = () => {
    if (formData.packs.length < 5) {
      const lastPack = formData.packs[formData.packs.length - 1];
      // Apply 10% discount per NFT for bulk packs
      const discountedPrice = Math.round(lastPack.pricePerNft * 0.9);
      setFormData(prev => ({
        ...prev,
        packs: [...prev.packs, {
          amount: lastPack.amount * 2,
          pricePerNft: discountedPrice
        }]
      }));
    }
  };

  // Remove pack
  const removePack = (index) => {
    if (formData.packs.length > 1) {
      setFormData(prev => ({
        ...prev,
        packs: prev.packs.filter((_, i) => i !== index)
      }));
    }
  };

  // Add whitelist address
  const addWhitelistAddress = () => {
    if (!newWhitelistAddress || !/^klv1[a-z0-9]{58}$/.test(newWhitelistAddress)) {
      setErrors(prev => ({ ...prev, newWhitelist: 'Invalid Klever address' }));
      return;
    }
    if (formData.whitelistAddresses.find(w => w.address === newWhitelistAddress)) {
      setErrors(prev => ({ ...prev, newWhitelist: 'Address already in whitelist' }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      whitelistAddresses: [...prev.whitelistAddresses, {
        address: newWhitelistAddress,
        limit: newWhitelistLimit || 0
      }]
    }));
    setNewWhitelistAddress('');
    setNewWhitelistLimit(0);
    setErrors(prev => ({ ...prev, newWhitelist: null }));
  };

  // Remove whitelist address
  const removeWhitelistAddress = (address) => {
    setFormData(prev => ({
      ...prev,
      whitelistAddresses: prev.whitelistAddresses.filter(w => w.address !== address)
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.receiverAddress) {
      newErrors.receiverAddress = 'Receiver address is required';
    } else if (!/^klv1[a-z0-9]{58}$/.test(formData.receiverAddress)) {
      newErrors.receiverAddress = 'Invalid Klever address';
    }

    if (formData.maxAmount < 1) {
      newErrors.maxAmount = 'Max amount must be at least 1';
    }

    formData.packs.forEach((pack, index) => {
      if (pack.amount < 1) {
        newErrors[`pack_${index}_amount`] = 'Amount must be at least 1';
      }
      if (pack.pricePerNft <= 0) {
        newErrors[`pack_${index}_price`] = 'Price must be greater than 0';
      }
    });

    // Validate times if set
    if (formData.startTime && formData.endTime) {
      if (new Date(formData.startTime) >= new Date(formData.endTime)) {
        newErrors.endTime = 'End time must be after start time';
      }
    }

    if (formData.whitelistStartTime && formData.whitelistEndTime) {
      if (new Date(formData.whitelistStartTime) >= new Date(formData.whitelistEndTime)) {
        newErrors.whitelistEndTime = 'Whitelist end must be after start';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Convert datetime string to Unix timestamp
  const toUnixTimestamp = (datetimeStr) => {
    if (!datetimeStr) return 0;
    return Math.floor(new Date(datetimeStr).getTime() / 1000);
  };

  // Configure ITO
  const handleConfigureITO = async () => {
    if (!validateForm()) return;

    setTransactionState('processing');
    setTransactionMessage('Building transaction...');

    try {
      // Build pack items with precision 6 for KLV
      // Price is per NFT on KleverChain
      const precision = 6;
      const packItems = formData.packs.map(pack => ({
        amount: pack.amount,
        price: Math.round(pack.pricePerNft * Math.pow(10, precision))
      }));

      // Build ConfigITO payload (Type 15)
      const configPayload = {
        kda: collection.assetId,
        receiverAddress: formData.receiverAddress,
        status: formData.status,
        maxAmount: formData.maxAmount,
        packInfo: {
          [formData.currencyId]: {
            packs: packItems
          }
        }
      };

      // Add optional timing fields
      if (formData.startTime) {
        configPayload.startTime = toUnixTimestamp(formData.startTime);
      }
      if (formData.endTime) {
        configPayload.endTime = toUnixTimestamp(formData.endTime);
      }

      // Add default limit per address if set
      if (formData.defaultLimitPerAddress > 0) {
        configPayload.defaultLimitPerAddress = formData.defaultLimitPerAddress;
      }

      // Add whitelist configuration
      if (formData.whitelistStatus === 1) {
        configPayload.whitelistStatus = 1; // Active
        if (formData.whitelistStartTime) {
          configPayload.whitelistStartTime = toUnixTimestamp(formData.whitelistStartTime);
        }
        if (formData.whitelistEndTime) {
          configPayload.whitelistEndTime = toUnixTimestamp(formData.whitelistEndTime);
        }
        if (formData.whitelistAddresses.length > 0) {
          configPayload.whitelistInfo = formData.whitelistAddresses.map(w => ({
            address: w.address,
            limit: w.limit
          }));
        }
      } else {
        configPayload.whitelistStatus = 0; // Inactive
      }

      const contracts = [
        {
          type: 15, // ConfigITO
          payload: configPayload
        }
      ];

      console.log('ConfigITO payload:', configPayload);

      setTransactionMessage('Please sign the transaction...');

      const unsignedTx = await web.buildTransaction(contracts);
      const signedTx = await web.signTransaction(unsignedTx);

      setTransactionMessage('Broadcasting transaction...');
      const response = await web.broadcastTransactions([signedTx]);

      console.log('Configure ITO response:', response);

      setTransactionState('success');
      setTransactionMessage('ITO configured successfully!');

      setTimeout(() => {
        onSuccess();
      }, 2000);

    } catch (error) {
      console.error('Error configuring ITO:', error);
      setTransactionState('error');
      setTransactionMessage(error.message || 'Failed to configure ITO');
    }
  };

  // Calculate total price for a pack (amount * pricePerNft)
  const getTotalPrice = (pack) => {
    return (pack.amount * pack.pricePerNft).toFixed(2);
  };

  // Calculate discount compared to single pack price per NFT
  const getDiscount = (pack, index) => {
    if (index === 0) return 0;
    const singlePrice = formData.packs[0].pricePerNft;
    const packPrice = pack.pricePerNft;
    const discount = ((singlePrice - packPrice) / singlePrice) * 100;
    return discount > 0 ? discount.toFixed(0) : 0;
  };

  return (
    <div className="cito-overlay">
      <div className="cito-modal" ref={modalRef}>
        <div className="cito-header">
          <h2 className="cito-title">
            <FaRocket /> Configure ITO
          </h2>
          <button
            className="cito-close"
            onClick={onClose}
            disabled={transactionState === 'processing'}
          >
            <FaTimes />
          </button>
        </div>

        <div className="cito-body">
          {/* Collection Info */}
          <div className="cito-collection-info">
            <div className="cito-collection-img">
              {collection.logo ? (
                <img
                  src={collection.logo.replace('ipfs://', 'https://ipfs.io/ipfs/')}
                  alt={collection.name}
                />
              ) : (
                <div className="cito-placeholder"><FaLayerGroup /></div>
              )}
            </div>
            <div className="cito-collection-details">
              <h3>{collection.name}</h3>
              <p>{collection.assetId}</p>
            </div>
            {existingIto && (
              <div className={`cito-badge ${existingIto.isActive ? 'active' : 'paused'}`}>
                {existingIto.isActive ? 'Live' : 'Paused'}
              </div>
            )}
          </div>

          {/* Basic Settings */}
          <div className="cito-section">
            <h4 className="cito-section-title">Basic Settings</h4>

            <div className="cito-form-group">
              <label><FaWallet /> Funds Receiver</label>
              <input
                type="text"
                className={`cito-input ${errors.receiverAddress ? 'error' : ''}`}
                placeholder="klv1..."
                value={formData.receiverAddress}
                onChange={(e) => updateField('receiverAddress', e.target.value)}
              />
              {errors.receiverAddress && <span className="cito-error">{errors.receiverAddress}</span>}
              <span className="cito-hint">Address that will receive sale funds</span>
            </div>

            <div className="cito-form-row">
              <div className="cito-form-group">
                <label><FaCoins /> Payment Currency</label>
                <select
                  className="cito-select"
                  value={formData.currencyId}
                  onChange={(e) => updateField('currencyId', e.target.value)}
                >
                  <option value="KLV">KLV</option>
                  <option value="USDT-F6I4">USDT</option>
                  <option value="DVK-1U9S">DVK</option>
                </select>
              </div>

              <div className="cito-form-group">
                <label>Max Mintable</label>
                <input
                  type="number"
                  className={`cito-input ${errors.maxAmount ? 'error' : ''}`}
                  min={1}
                  value={formData.maxAmount}
                  onChange={(e) => updateField('maxAmount', parseInt(e.target.value) || 0)}
                />
                {errors.maxAmount && <span className="cito-error">{errors.maxAmount}</span>}
              </div>
            </div>

            <div className="cito-form-group">
              <label>ITO Status</label>
              <div className="cito-status-toggle">
                <button
                  className={`cito-status-btn ${formData.status === 1 ? 'active' : ''}`}
                  onClick={() => updateField('status', 1)}
                >
                  <FaPlay /> Active
                </button>
                <button
                  className={`cito-status-btn ${formData.status === 0 ? 'active paused' : ''}`}
                  onClick={() => updateField('status', 0)}
                >
                  <FaPause /> Paused
                </button>
              </div>
            </div>
          </div>

          {/* Pack Pricing */}
          <div className="cito-section">
            <div className="cito-section-header">
              <h4 className="cito-section-title">Pack Pricing</h4>
              {formData.packs.length < 5 && (
                <button className="cito-add-btn" onClick={addPack}>
                  <FaPlus /> Add Pack
                </button>
              )}
            </div>

            <div className="cito-packs">
              {formData.packs.map((pack, index) => (
                <div key={index} className="cito-pack">
                  <div className="cito-pack-header">
                    <span>Pack {index + 1}</span>
                    {formData.packs.length > 1 && (
                      <button className="cito-remove-btn" onClick={() => removePack(index)}>
                        <FaTrash />
                      </button>
                    )}
                  </div>
                  <div className="cito-pack-inputs">
                    <div className="cito-pack-field">
                      <label>NFTs</label>
                      <input
                        type="number"
                        className="cito-input"
                        min={1}
                        value={pack.amount}
                        onChange={(e) => updatePack(index, 'amount', parseInt(e.target.value) || 1)}
                      />
                    </div>
                    <div className="cito-pack-field">
                      <label>Price/NFT ({formData.currencyId})</label>
                      <input
                        type="number"
                        className="cito-input"
                        min={0.01}
                        step={0.01}
                        value={pack.pricePerNft}
                        onChange={(e) => updatePack(index, 'pricePerNft', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                  <div className="cito-pack-stats">
                    <span className="cito-per-nft">Total: {getTotalPrice(pack)} {formData.currencyId}</span>
                    {index > 0 && getDiscount(pack, index) > 0 && (
                      <span className="cito-discount">-{getDiscount(pack, index)}%</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Advanced Settings - Collapsible */}
          <div className="cito-section cito-collapsible">
            <button className="cito-collapse-btn" onClick={() => setShowAdvanced(!showAdvanced)}>
              <div className="cito-collapse-title">
                <FaClock /> Advanced Settings (Times & Limits)
              </div>
              {showAdvanced ? <FaChevronUp /> : <FaChevronDown />}
            </button>

            {showAdvanced && (
              <div className="cito-collapse-content">
                <div className="cito-form-row">
                  <div className="cito-form-group">
                    <label><FaCalendarAlt /> Start Time</label>
                    <input
                      type="datetime-local"
                      className="cito-input"
                      value={formData.startTime}
                      onChange={(e) => updateField('startTime', e.target.value)}
                    />
                    <span className="cito-hint">Leave empty for immediate start</span>
                  </div>

                  <div className="cito-form-group">
                    <label><FaCalendarAlt /> End Time</label>
                    <input
                      type="datetime-local"
                      className={`cito-input ${errors.endTime ? 'error' : ''}`}
                      value={formData.endTime}
                      onChange={(e) => updateField('endTime', e.target.value)}
                    />
                    {errors.endTime && <span className="cito-error">{errors.endTime}</span>}
                    <span className="cito-hint">Leave empty for no end date</span>
                  </div>
                </div>

                <div className="cito-form-group">
                  <label><FaUsers /> Default Limit Per Address</label>
                  <input
                    type="number"
                    className="cito-input"
                    min={0}
                    value={formData.defaultLimitPerAddress}
                    onChange={(e) => updateField('defaultLimitPerAddress', parseInt(e.target.value) || 0)}
                  />
                  <span className="cito-hint">Max NFTs any address can mint. 0 = unlimited</span>
                </div>
              </div>
            )}
          </div>

          {/* Whitelist Settings - Collapsible */}
          <div className="cito-section cito-collapsible">
            <button className="cito-collapse-btn" onClick={() => setShowWhitelist(!showWhitelist)}>
              <div className="cito-collapse-title">
                <FaUserShield /> Whitelist Settings
              </div>
              {showWhitelist ? <FaChevronUp /> : <FaChevronDown />}
            </button>

            {showWhitelist && (
              <div className="cito-collapse-content">
                <div className="cito-form-group">
                  <label>Whitelist Status</label>
                  <div className="cito-status-toggle">
                    <button
                      className={`cito-status-btn ${formData.whitelistStatus === 0 ? 'active' : ''}`}
                      onClick={() => updateField('whitelistStatus', 0)}
                    >
                      <FaToggleOff /> Disabled
                    </button>
                    <button
                      className={`cito-status-btn ${formData.whitelistStatus === 1 ? 'active whitelist' : ''}`}
                      onClick={() => updateField('whitelistStatus', 1)}
                    >
                      <FaToggleOn /> Enabled
                    </button>
                  </div>
                  <span className="cito-hint">When enabled, only whitelisted addresses can mint during whitelist period</span>
                </div>

                {formData.whitelistStatus === 1 && (
                  <>
                    <div className="cito-form-row">
                      <div className="cito-form-group">
                        <label><FaCalendarAlt /> Whitelist Start</label>
                        <input
                          type="datetime-local"
                          className="cito-input"
                          value={formData.whitelistStartTime}
                          onChange={(e) => updateField('whitelistStartTime', e.target.value)}
                        />
                      </div>

                      <div className="cito-form-group">
                        <label><FaCalendarAlt /> Whitelist End</label>
                        <input
                          type="datetime-local"
                          className={`cito-input ${errors.whitelistEndTime ? 'error' : ''}`}
                          value={formData.whitelistEndTime}
                          onChange={(e) => updateField('whitelistEndTime', e.target.value)}
                        />
                        {errors.whitelistEndTime && <span className="cito-error">{errors.whitelistEndTime}</span>}
                      </div>
                    </div>

                    <div className="cito-whitelist-section">
                      <label>Whitelisted Addresses ({formData.whitelistAddresses.length})</label>

                      <div className="cito-whitelist-add">
                        <input
                          type="text"
                          className={`cito-input ${errors.newWhitelist ? 'error' : ''}`}
                          placeholder="klv1..."
                          value={newWhitelistAddress}
                          onChange={(e) => {
                            setNewWhitelistAddress(e.target.value);
                            setErrors(prev => ({ ...prev, newWhitelist: null }));
                          }}
                        />
                        <input
                          type="number"
                          className="cito-input cito-input-small"
                          placeholder="Limit (0=unlimited)"
                          min={0}
                          value={newWhitelistLimit}
                          onChange={(e) => setNewWhitelistLimit(parseInt(e.target.value) || 0)}
                        />
                        <button className="cito-add-btn" onClick={addWhitelistAddress}>
                          <FaPlus />
                        </button>
                      </div>
                      {errors.newWhitelist && <span className="cito-error">{errors.newWhitelist}</span>}

                      {formData.whitelistAddresses.length > 0 && (
                        <div className="cito-whitelist-list">
                          {formData.whitelistAddresses.map((item, index) => (
                            <div key={index} className="cito-whitelist-item">
                              <span className="cito-whitelist-addr">
                                {item.address.slice(0, 10)}...{item.address.slice(-6)}
                              </span>
                              <span className="cito-whitelist-limit">
                                Limit: {item.limit || '∞'}
                              </span>
                              <button
                                className="cito-remove-btn"
                                onClick={() => removeWhitelistAddress(item.address)}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Info Notice */}
          <div className="cito-info">
            <FaInfoCircle />
            <span>
              {existingIto
                ? 'This will update your existing ITO configuration.'
                : 'Configuring an ITO allows users to mint NFTs from your collection.'}
            </span>
          </div>

          {/* Transaction Status */}
          {transactionState && (
            <div className={`cito-tx-status cito-tx-${transactionState}`}>
              {transactionState === 'processing' && <FaSpinner className="cito-spin" />}
              {transactionState === 'success' && <FaCheckCircle />}
              {transactionState === 'error' && <FaTimesCircle />}
              <span>{transactionMessage}</span>
            </div>
          )}
        </div>

        <div className="cito-footer">
          <button
            className="cito-btn cito-btn-cancel"
            onClick={onClose}
            disabled={transactionState === 'processing'}
          >
            Cancel
          </button>
          <button
            className="cito-btn cito-btn-submit"
            onClick={handleConfigureITO}
            disabled={transactionState === 'processing' || transactionState === 'success'}
          >
            {transactionState === 'processing' ? (
              <><FaSpinner className="cito-spin" /> Configuring...</>
            ) : existingIto ? (
              <><FaRocket /> Update ITO</>
            ) : (
              <><FaRocket /> Create ITO</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigITOModal;
